import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { ALL_SERVICE_OPTIONS, BUSINESS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-teal-line bg-panel px-4 py-3.5 text-base text-white placeholder:text-chrome/50 transition-colors duration-200 focus:border-mint focus:outline-none sm:text-sm";

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="spec-label mb-2 block">{label}</label>
    {children}
  </div>
);

const QuoteForm = () => {
  const [params] = useSearchParams();
  const preselect = params.get("service") || "";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", vehicle: "",
    service: ALL_SERVICE_OPTIONS.some((s) => s.id === preselect) ? preselect : "",
    message: "",
  });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        service: ALL_SERVICE_OPTIONS.find((s) => s.id === form.service)?.name || form.service,
        attachment_name: file?.name || null,
      });
      setSent(true);
      toast.success("Quote request sent. We'll be in touch shortly.");
    } catch {
      toast.error("Could not send your request. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-teal-line bg-panel p-10 text-center" data-testid="quote-success">
        <CheckCircle2 className="mx-auto h-10 w-10 text-mint" strokeWidth={1.5} aria-hidden="true" />
        <h3 className="mt-5 font-display text-2xl font-bold uppercase">Request received</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-chrome">
          Thanks {form.name || "there"} — we have your {ALL_SERVICE_OPTIONS.find((s) => s.id === form.service)?.name || "enquiry"} request
          {form.vehicle ? ` for the ${form.vehicle}` : ""}. We reply within business hours, 9:30 AM – 6:00 PM daily.
          Prefer to talk now? Call <a className="text-mint" href={BUSINESS.phoneHref}>{BUSINESS.phoneDisplay}</a>.
        </p>
        <button data-testid="quote-reset-btn" onClick={() => setSent(false)} className="btn-ghost mt-8">Send another</button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="border border-teal-line bg-panel p-6 sm:p-10" data-testid="quote-form">
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Name" htmlFor="qf-name">
          <input id="qf-name" data-testid="quote-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
        </Field>
        <Field label="Email" htmlFor="qf-email">
          <input id="qf-email" data-testid="quote-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </Field>
        <Field label="Phone" htmlFor="qf-phone">
          <input id="qf-phone" data-testid="quote-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" />
        </Field>
        <Field label="Vehicle make / model" htmlFor="qf-vehicle">
          <input id="qf-vehicle" data-testid="quote-vehicle-input" required className={inputCls} value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. Porsche 911 Carrera" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Service required" htmlFor="qf-service">
            <select id="qf-service" data-testid="quote-service-select" required className={inputCls} value={form.service} onChange={set("service")}>
              <option value="" disabled>Select a service</option>
              {ALL_SERVICE_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Message" htmlFor="qf-message">
            <textarea id="qf-message" data-testid="quote-message-input" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Condition of the car, timing, anything we should know" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Photo of the vehicle (optional)" htmlFor="qf-file">
            <input
              id="qf-file"
              data-testid="quote-file-input"
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full font-mono text-xs text-chrome file:mr-4 file:border file:border-teal-line file:bg-teal-deep file:px-4 file:py-2.5 file:font-mono file:text-xs file:uppercase file:tracking-[0.15em] file:text-mint hover:file:border-mint"
            />
          </Field>
          {file && <p className="mt-2 font-mono text-[11px] text-mint" data-testid="quote-file-name">{file.name}</p>}
        </div>
      </div>
      <button type="submit" data-testid="quote-submit-btn" disabled={sending} className="btn-primary mt-8 w-full disabled:opacity-60 sm:w-auto">
        {sending ? "Sending…" : "Request my quote"}
      </button>
    </form>
  );
};

export default QuoteForm;
